import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Pencil } from "lucide-react";
import { formatDate } from "@/lib/format";
import { EnquiryStatusBadge } from "@/components/enquiries/enquiry-status-badge";
import { type EnquiryDetailDto, type UpdateEnquiryRequest, enquiriesApi } from "@/lib/enquiries";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { EmployeePicker } from "@/components/employee-picker";
import { ClientPicker, type ClientPickerValue } from "@/components/client-picker";
import { toast } from "@/hooks/use-toast";

export interface EnquiryHeaderProps {
  enquiry: EnquiryDetailDto;
}

interface HeaderField {
  label: string;
  value: React.ReactNode;
}

export function EnquiryHeader({ enquiry }: EnquiryHeaderProps) {
  const [open, setOpen] = useState(false);
  const [ownerId, setOwnerId] = useState<number | null>(enquiry.ownerEmployeeId);
  const [ownerName, setOwnerName] = useState<string | null>(enquiry.ownerEmployeeName);
  const [client, setClient] = useState<ClientPickerValue | null>(
    enquiry.clientId
      ? { clientId: enquiry.clientId, fullName: enquiry.clientName ?? "", emailAddress: null, companyId: null, companyName: null }
      : null
  );
  const [description, setDescription] = useState(enquiry.description ?? "");

  const qc = useQueryClient();
  const update = useMutation({
    mutationFn: async () => {
      if (!enquiry.rowVersionBase64) return;
      const patchBody: UpdateEnquiryRequest = {
        ownerEmployeeId: ownerId,
        clientId: client?.clientId ?? null,
        description: description.trim() || null,
        rowVersionBase64: enquiry.rowVersionBase64,
      };
      await enquiriesApi.patch(enquiry.enquiryId, patchBody);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["enquiry", String(enquiry.enquiryId)] });
      void qc.invalidateQueries({ queryKey: ["enquiry", enquiry.enquiryId] });
      toast.success("Enquiry updated.");
      setOpen(false);
    },
    onError: (err: Error) => {
      toast.error(`Update failed: ${err.message}`);
    },
  });

  const handleOpen = () => {
    setOwnerId(enquiry.ownerEmployeeId);
    setOwnerName(enquiry.ownerEmployeeName);
    setClient(enquiry.clientId
      ? { clientId: enquiry.clientId, fullName: enquiry.clientName ?? "", emailAddress: null, companyId: null, companyName: null }
      : null);
    setDescription(enquiry.description ?? "");
    setOpen(true);
  };

  const fields: HeaderField[] = [
    {
      label: "Enquiry Ref",
      value: (
        <span className="inline-flex items-center gap-2">
          <span className="font-normal">#{enquiry.enquiryId}</span>
          <EnquiryStatusBadge status={enquiry.status} />
        </span>
      ),
    },
    { label: "Customer Name", value: enquiry.clientName ?? "—" },
    { label: "Owner", value: enquiry.ownerEmployeeName ?? "—" },
    { label: "Branch", value: enquiry.branchId != null ? `Branch #${enquiry.branchId}` : "—" },
    { label: "Source System", value: enquiry.sourceSystem ?? "—" },
    { label: "External ID", value: enquiry.externalId ?? "—" },
    { label: "Created On", value: enquiry.createdOn ? formatDate(enquiry.createdOn) : "—" },
  ];

  return (
    <header className="mb-4 relative bg-white rounded-lg px-6 py-4 shadow-sm border border-slate-200 group">
      <Button
        variant="ghost"
        size="icon"
        className="absolute top-2 right-2"
        onClick={handleOpen}
        title="Edit Enquiry"
      >
        <Pencil className="h-4 w-4 text-slate-500" />
      </Button>

      <dl className="grid grid-cols-2 gap-x-6 gap-y-3 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-7 xl:gap-x-4">
        {fields.map((f) => (
          <div key={f.label} className="min-w-0">
            <dt className="text-[11px] font-normal text-slate-900 mb-1">
              {f.label}
            </dt>
            <dd className="text-sm font-normal text-slate-800">{f.value}</dd>
          </div>
        ))}
      </dl>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Enquiry</DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div className="space-y-2">
              <Label>Client</Label>
              <ClientPicker value={client} onChange={setClient} />
            </div>
            <div className="space-y-2">
              <Label>Owner</Label>
              <div className="border rounded-md">
                <EmployeePicker
                  value={ownerId}
                  valueLabel={ownerName}
                  onChange={(id, emp) => {
                    setOwnerId(id);
                    setOwnerName(emp?.fullName ?? null);
                  }}
                  placeholder="Search for an employee..."
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Enquiry description"
                className="min-h-[120px] resize-none"
                maxLength={500}
              />
              <div className="text-right text-xs text-muted-foreground mt-1">
                {description.length} / 500
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={() => update.mutate()} disabled={update.isPending}>
              {update.isPending ? "Saving..." : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </header>
  );
}
